import React, { Component, createRef } from 'react';

// CSS
import '../Subscriptions/Subscription.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, getCurrentUser } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Modal, Confirm } from '../../components/UI/Overlays';
import { EmptyState, TableWait, Tip } from '../../components/UI/Feedback';
import { Toolbar } from '../../components/UI/Controls';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

// Properties
const properties = require('../../properties.json');

class SchoolStages extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        branchID: getCurrentUser()?.branchId,
        globalFilter: null,
        userInput: null,
        data: [],
        editableData: null,
        visible: false,

        // the rows are on their way
        tableLoading: true,

        // how many materials hang off each stage. The server refuses to
        // delete a stage that still has some, so the count is shown on the
        // row and in the question rather than only coming back as an error.
        materialCounts: {},

        // adding happens in a dialog from this page now, not in a box wedged
        // into the table's header
        addOpen: false,
        saving: false,
        editSaving: false,

        // the stage the teacher asked to delete, held while the confirm is up
        doomed: null,
        deleting: false,
    }

    getStages() {
        const user = getCurrentUser();
        if (!user) return;
        this.setState({ tableLoading: true });
        apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: user.userId } })
            .then(stages => {
                this.setState({ data: stages, tableLoading: false });
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ tableLoading: false });
            });
    }

    // Every material the teacher has, counted per stage. Same lookup the rest
    // of the dashboard already calls; each material carries its own stageId.
    getMaterialCounts = () => {
        const user = getCurrentUser();
        if (!user) return;
        apiFetch("/lookup/materials", { query: { branchId: user.branchId, teacherUserId: user.userId } })
            .then(materials => {
                const materialCounts = {};
                materials.forEach(m => {
                    materialCounts[m.stageId] = (materialCounts[m.stageId] || 0) + 1;
                });
                this.setState({ materialCounts });
            })
            .catch(error => console.error('Error:', error));
    }

    componentDidMount() {
        document.title = properties.site_name + " | المراحل الدراسية";
        // Redirect To Sign In Page If The User Is Already Logged Out & Remove The Data If It Exists
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
        }
        this.getStages();
        this.getMaterialCounts();
    }

    addStage = () => {
        var _this = this;
        if (!this.state.userInput) return;
        this.setState({ saving: true });
        apiFetch("/lookup/school-stages", {
            method: "POST",
            body: {
                name: this.state.userInput,
                branchId: this.state.branchID,
            },
        })
            .then(() => {
                _this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تمت الإضافة بنجاح' });
                // the save finished the job, so the dialog closes and the
                // table behind it refetches
                this.setState({ userInput: null, addOpen: false, saving: false });
                this.getStages();
                this.getMaterialCounts();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ saving: false });
                _this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    render() {
        const { tableLoading, doomed, deleting, data } = this.state;

        // The browser's own confirm box is gone; the question is asked in the
        // product's own confirm card instead. The delete behind it is the
        // same call it always was.
        var deleteStage = (row) => {
            var _this = this;
            this.setState({ deleting: true });
            apiFetch(`/lookup/school-stages/${row.id}`, { method: "DELETE" })
                .then(() => {
                    this.setState({ doomed: null, deleting: false });
                    this.getStages();
                    this.getMaterialCounts();
                    _this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم الحذف بنجاح' });
                })
                .catch(error => {
                    console.error('Error:', error);
                    this.setState({ doomed: null, deleting: false });
                    // the server refuses a stage that still has materials, and
                    // says so in its own words -- that message is the useful one
                    _this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
                });
        }

        var editStage = () => {
            var _this = this;
            this.setState({ editSaving: true });
            apiFetch(`/lookup/school-stages/${this.state.editableData.id}`, {
                method: "PUT",
                body: {
                    name: this.state.editableData.name,
                    branchId: this.state.branchID,
                },
            })
                .then(() => {
                    _this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم التعديل بنجاح' });
                    this.getStages();
                    this.setState({ visible: false, editSaving: false });
                })
                .catch(error => {
                    console.error('Error:', error);
                    this.setState({ editSaving: false });
                    _this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
                });
        }

        const openAdd = () => this.setState({ addOpen: true, userInput: null });
        const closeAdd = () => this.setState({ addOpen: false, userInput: null });

        // a name is the whole record, so that is the whole condition
        const canSave = !!(this.state.userInput && this.state.userInput.trim());
        const canSaveEdit = !!(this.state.editableData && this.state.editableData.name && this.state.editableData.name.trim());

        const countOf = (row) => this.state.materialCounts[row.id] || 0;

        // what the stage actually holds -- and the reason a delete will be
        // refused, said before the teacher presses it
        const materialsBody = (row) => {
            const n = countOf(row);
            if (!n) return <span className="ui-bits is-none">لسه مفيهاش مواد</span>;
            return (
                <span className="ui-bits">
                    <span className="bit" title="مواد">
                        <i className="pi pi-book"></i>
                        {n}
                    </span>
                </span>
            );
        };

        const actionBodyTemplate = (rowData) => {
            return (
                <React.Fragment>
                    <Tip text="تعديل">
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => this.setState({ visible: true, editableData: { ...rowData } })} />
                    </Tip>
                    <Tip text="حذف">
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => this.setState({ doomed: rowData })} />
                    </Tip>
                </React.Fragment>
            );
        }

        // the question changes with what the stage is carrying: a stage with
        // materials cannot be deleted at all, so the card says so instead of
        // asking a question whose answer the server will refuse
        const doomedCount = doomed ? countOf(doomed) : 0;

        return (
            <>
                <Header
                    pageName="المراحل الدراسية"
                    action={{ label: "إضافة مرحلة", icon: "pi pi-plus", onClick: openAdd }}
                />
                <Toast ref={this.toast}></Toast>

                <Modal
                    visible={this.state.addOpen}
                    onHide={closeAdd}
                    title="إضافة مرحلة دراسية"
                    footer={
                        <>
                            <button
                                type="button"
                                className="ui-btn is-gold"
                                disabled={!canSave || this.state.saving}
                                onClick={this.addStage}
                            >
                                {this.state.saving ? "جاري الإضافة..." : "إضافة"}
                            </button>
                            <button type="button" className="ui-btn is-secondary" onClick={closeAdd}>إلغاء</button>
                        </>
                    }
                >
                    <div className="p-inputgroup">
                        <span className="p-float-label">
                            <InputText
                                id="stage-name"
                                value={this.state.userInput || ''}
                                onChange={(e) => this.setState({ userInput: e.target.value })}
                                placeholder="مثال: الصف الأول الثانوي"
                            />
                            <label htmlFor="stage-name">إسم المرحلة الدراسية</label>
                        </span>
                    </div>
                </Modal>

                <Modal
                    visible={this.state.visible}
                    onHide={() => this.setState({ visible: false })}
                    title="تعديل المرحلة الدراسية"
                    footer={
                        <>
                            <button
                                type="button"
                                className="ui-btn is-gold"
                                disabled={!canSaveEdit || this.state.editSaving}
                                onClick={editStage}
                            >
                                {this.state.editSaving ? "جاري الحفظ..." : "حفظ"}
                            </button>
                            <button type="button" className="ui-btn is-secondary" onClick={() => this.setState({ visible: false })}>إلغاء</button>
                        </>
                    }
                >
                    {this.state.editableData !== null &&
                        <div className="p-inputgroup">
                            <span className="p-float-label">
                                <InputText
                                    id="stage-name-edit"
                                    value={this.state.editableData.name}
                                    onChange={(e) => this.setState({ editableData: { ...this.state.editableData, name: e.target.value } })}
                                />
                                <label htmlFor="stage-name-edit">إسم المرحلة الدراسية</label>
                            </span>
                        </div>
                    }
                </Modal>

                <Confirm
                    visible={!!doomed}
                    onHide={() => this.setState({ doomed: null })}
                    name={doomed ? doomed.name : ""}
                    note={
                        doomedCount
                            ? `المرحلة دي عليها ${doomedCount} مادة، والحذف مش هيتم غير لما تحذف موادها الأول.`
                            : "المرحلة هتتشال نهائياً، ومش هينفع ترجع عنها."
                    }
                    busy={deleting}
                    onConfirm={() => deleteStage(doomed)}
                />

                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                <Toolbar
                                    value={this.state.globalFilter || ''}
                                    onSearch={(v) => this.setState({ globalFilter: v })}
                                    placeholder="بحث في المراحل الدراسية"
                                    count={tableLoading ? null : `${data.length} مرحلة`}
                                />

                                <DataTable
                                    sortable
                                    globalFilter={this.state.globalFilter}
                                    className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                                    value={data} paginator rows={10} rowsPerPageOptions={[10, 20, 50]}
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                                    emptyMessage={
                                        tableLoading ? (
                                            <TableWait />
                                        ) : (
                                            <EmptyState
                                                title="لا يوجد مراحل دراسية"
                                                hint="ابدأ بإضافة أول مرحلة، وبعدها تقدر تضيف موادها"
                                                icon="pi pi-sitemap"
                                                action={{ label: "إضافة مرحلة", onClick: openAdd }}
                                            />
                                        )
                                    }>
                                    <Column sortable field="name" header="إسم المرحلة الدراسية"></Column>
                                    <Column body={materialsBody} header="المواد"></Column>
                                    <Column header="تعديل / حذف" body={actionBodyTemplate}></Column>
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default SchoolStages;
